import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@fluent/ui';
import { useCompletions } from '../progress/hooks.js';
import { detectPlatform, isInAppBrowser } from './installPromptStore.js';
import { openInstallModal } from './installModalStore.js';
import './InstallPrompt.css';

/**
 * InstallPrompt — the "put fluent.ge on your home screen" banner.
 *
 * Asked every 4 finished lessons — at 4, 8, 12… — until they install
 * (Tornike, 2026-09-21: "every 4 lessons"). The first ask waits until they are
 * engaged; after that, "no thanks" means "not now", and the banner comes back
 * 4 lessons later. Never shown once running installed.
 *
 * Two phones, two different asks, because the platforms allow different
 * things:
 *
 *   Android (Chrome) → the browser fires `beforeinstallprompt`; our button
 *                      calls `.prompt()` and Chrome shows its own one-tap
 *                      "Install" dialog. A real install.
 *   iPhone (Safari)  → Apple lets no website install itself. The banner can
 *                      only open the InstallModal, which shows the two steps
 *                      (Share → "Add to Home Screen") with pictures.
 *
 * Not shown in social apps' built-in browsers: they can do neither, and the
 * iPhone steps would point at a Share button that is not there.
 *
 * Since 2026-09-21 this is how fluent.ge reaches home screens at all — there
 * are no store apps (DECISIONS 2026-09-21).
 */

/** Completed-lesson count at the last time they answered the banner. */
const ASKED_AT_KEY = 'fluent.install-prompt.asked-at.v2';
/** Android told us the install happened: never ask again. */
const INSTALLED_KEY = 'fluent.install-prompt.installed.v1';
const EVERY_N_LESSONS = 4;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

function readAskedAt(): number {
  if (typeof window === 'undefined') return Number.POSITIVE_INFINITY;
  try {
    const n = Number(window.localStorage.getItem(ASKED_AT_KEY));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function readInstalled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(INSTALLED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Private mode / blocked storage: the worst case is asking again sooner. */
function persist(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const completions = useCompletions();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [askedAt, setAskedAt] = useState(readAskedAt);
  const [installed, setInstalled] = useState(readInstalled);
  // Decided once: none of these change during a visit, and 'installed' covers
  // "already running from the home screen".
  const [platform] = useState(detectPlatform);
  const [inApp] = useState(isInAppBrowser);

  useEffect(() => {
    if (typeof window === 'undefined' || platform === 'installed') return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Wipe the deferred event on successful install (Android fires this).
    const onInstalled = () => {
      setDeferred(null);
      setInstalled(true);
      persist(INSTALLED_KEY, '1');
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [platform]);

  const completedCount = Object.keys(completions.data ?? {}).length;
  // Due at 4 finished lessons, then every 4 more after they last answered.
  const due = completedCount >= EVERY_N_LESSONS && completedCount >= askedAt + EVERY_N_LESSONS;
  const canInstall = deferred !== null; // Android: a real install is on offer
  const canShowSteps = platform === 'ios' && !inApp; // iPhone: steps that will work

  if (installed || !due || (!canInstall && !canShowSteps)) return null;

  /** "Not now": come back after the next 4 lessons. */
  const dismiss = () => {
    setAskedAt(completedCount);
    persist(ASKED_AT_KEY, String(completedCount));
  };

  const onInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      // Said no to Chrome's dialog: same as "no thanks" — ask again later.
      if (choice.outcome === 'dismissed') dismiss();
      setDeferred(null);
    } catch {
      // Browser refused — drop the prompt silently.
      setDeferred(null);
    }
  };

  // iPhone: open the step-by-step modal and put the banner away until the
  // next 4 lessons. We can't know whether they finished the steps — once they
  // open fluent.ge from the home-screen icon it runs installed and the banner
  // never shows there.
  const onShowSteps = () => {
    openInstallModal();
    dismiss();
  };

  return (
    <div
      className="fluent-install-prompt fluent-theme-light"
      role="region"
      aria-label={t('install_prompt.aria')}
      data-testid="install-prompt"
    >
      <div className="fluent-install-prompt__body">
        <p className="fluent-install-prompt__title">{t('install_prompt.title')}</p>
        <p className="fluent-install-prompt__message">
          {canInstall ? t('install_prompt.message_android') : t('install_prompt.message_ios')}
        </p>
      </div>
      <div className="fluent-install-prompt__actions">
        <Button variant="ghost" size="sm" onClick={dismiss}>
          {t('install_prompt.dismiss')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={canInstall ? onInstall : onShowSteps}
          data-testid="install-cta"
        >
          {canInstall ? t('install_prompt.cta_install') : t('install_prompt.cta_how')}
        </Button>
      </div>
    </div>
  );
}
